import { useState, useEffect } from 'react';

export type Breakpoint = 'mobile' | 'tablet' | 'laptop' | 'desktop';

interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isLaptop: boolean;
  isDesktop: boolean;
  activeBreakpoint: Breakpoint;
  width: number;
  height: number;
}

/**
 * Custom hook for responsive design
 * Provides state variables for different screen sizes and orientation
 */
export const useResponsive = (): ResponsiveState => {
  // Define breakpoints (matching our CSS)
  const MOBILE_MAX = 767; // up to 767px
  const TABLET_MIN = 768; // from 768px
  const TABLET_MAX = 1023; // up to 1023px
  const LAPTOP_MIN = 1024; // from 1024px
  const LAPTOP_MAX = 1199; // up to 1199px
  const DESKTOP_MIN = 1200; // from 1200px

  // Initial state setup (with default for SSR)
  const [state, setState] = useState<ResponsiveState>({
    isMobile: false,
    isTablet: false,
    isLaptop: false,
    isDesktop: false,
    activeBreakpoint: 'mobile',
    width: 0,
    height: 0,
  });

  useEffect(() => {
    // Initial measurement
    updateDimensions();

    // Set up the resize event listener
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
    };
  }, []);

  /**
   * Update all responsive state based on current window dimensions
   */
  const updateDimensions = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Determine active breakpoint and flags
    const isMobile = width <= MOBILE_MAX;
    const isTablet = width >= TABLET_MIN && width <= TABLET_MAX;
    const isLaptop = width >= LAPTOP_MIN && width <= LAPTOP_MAX;
    const isDesktop = width >= DESKTOP_MIN;

    let activeBreakpoint: Breakpoint = 'mobile';
    if (isDesktop) activeBreakpoint = 'desktop';
    else if (isLaptop) activeBreakpoint = 'laptop';
    else if (isTablet) activeBreakpoint = 'tablet';

    setState({
      isMobile,
      isTablet,
      isLaptop,
      isDesktop,
      activeBreakpoint,
      width,
      height,
    });
  };

  return state;
};

export default useResponsive; 